import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Activity } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

/**
 * Public, no-auth pricing page. Mounted at /pricing.
 * Pulls /api/billing/public-plans (no JWT). Visitors can hit Sign up
 * with the chosen plan slug pre-selected via query string.
 */
export default function PricingPage() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [billing, setBilling] = useState("monthly");

  useEffect(() => {
    api
      .get("/billing/public-plans")
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => setPlans([]));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="public-pricing-page">
      <header className="border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-[hsl(var(--accent))]" />
            <span className="font-bold tracking-tight text-lg">Elswitch</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                {t("nav.login", "Log in")}
              </Button>
            </Link>
            <Link to="/signup">
              <Button size="sm">
                {t("nav.signup", "Sign up")}
                <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            {t("pricing.heroTitle", "Honest, simple softswitch pricing")}
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {t(
              "pricing.heroSub",
              "Pay for what you actually use — concurrent calls. All codecs included. No per-minute charges. No feature gates on basics."
            )}
          </p>
          <div className="inline-flex mt-6 rounded-md border border-border p-0.5">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              className={`px-3 py-1 text-xs rounded ${
                billing === "monthly"
                  ? "bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]"
                  : "text-muted-foreground"
              }`}
              data-testid="pricing-billing-monthly"
            >
              {t("pricing.monthly", "Monthly")}
            </button>
            <button
              type="button"
              onClick={() => setBilling("yearly")}
              className={`px-3 py-1 text-xs rounded ${
                billing === "yearly"
                  ? "bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]"
                  : "text-muted-foreground"
              }`}
              data-testid="pricing-billing-yearly"
            >
              {t("pricing.yearly", "Yearly")}{" "}
              <span className="text-[10px] text-[hsl(var(--ok))]">−20%</span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {plans.map((p) => {
            const isFeatured = p.slug === "growth";
            const metric = p.billing_metric || "flat";
            // For per-unit metrics show per-unit rate; for flat show $/mo or $/yr.
            let bigPrice, smallSuffix, secondaryNote;
            if (metric === "per_extension") {
              bigPrice = `$${p.price_per_unit_usd.toFixed(2)}`;
              smallSuffix = "/ext/mo";
              secondaryNote = p.min_monthly_usd > 0
                ? t("pricing.minOf", "${{n}}/mo minimum", { n: p.min_monthly_usd.toFixed(0) })
                : null;
            } else if (metric === "per_concurrent_call") {
              bigPrice = `$${p.price_per_unit_usd.toFixed(2)}`;
              smallSuffix = "/cc/mo";
              secondaryNote = p.min_monthly_usd > 0
                ? t("pricing.minOf", "${{n}}/mo minimum", { n: p.min_monthly_usd.toFixed(0) })
                : null;
            } else {
              const price =
                billing === "yearly" ? p.price_yearly_usd : p.price_monthly_usd;
              bigPrice = `$${price.toFixed(0)}`;
              smallSuffix = billing === "yearly" ? "/yr" : "/mo";
              secondaryNote = null;
            }
            return (
              <Card
                key={p.id}
                className={`relative ${
                  isFeatured
                    ? "border-[hsl(var(--accent))] ring-2 ring-[hsl(var(--accent))]/30 md:scale-105"
                    : ""
                }`}
                data-testid={`pricing-card-${p.slug}`}
              >
                {isFeatured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[hsl(var(--accent))] text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded text-background">
                    {t("pricing.popular", "Most popular")}
                  </div>
                )}
                <CardContent className="p-5 space-y-3">
                  <h3 className="font-bold text-lg">{p.name}</h3>
                  <p className="text-xs text-muted-foreground min-h-[32px]">
                    {p.description}
                  </p>
                  <div>
                    <span className="text-3xl font-bold">{bigPrice}</span>
                    <span className="text-xs text-muted-foreground ml-1">
                      {smallSuffix}
                    </span>
                    {secondaryNote && (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        {secondaryNote}
                      </div>
                    )}
                    {(p.volume_discount_tiers || []).length > 0 && (
                      <div className="text-[10px] text-[hsl(var(--ok))] mt-0.5">
                        {t("pricing.volumeDiscounts", "Volume discounts")}:{" "}
                        {(p.volume_discount_tiers || []).map(t =>
                          `${t.threshold}+ → −${t.percent}%`
                        ).join(", ")}
                      </div>
                    )}
                  </div>
                  <ul className="space-y-1.5 text-xs">
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-[hsl(var(--ok))] shrink-0" />
                      <span>
                        <strong>{p.limits.concurrent_calls}</strong>{" "}
                        {t("pricing.cc", "concurrent calls")}
                      </span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-[hsl(var(--ok))] shrink-0" />
                      <span>
                        <strong>{p.limits.max_users || "Unlimited"}</strong>{" "}
                        {t("pricing.users", "SIP users")}
                      </span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-[hsl(var(--ok))] shrink-0" />
                      <span>
                        <strong>{p.limits.max_trunks || "Unlimited"}</strong>{" "}
                        {t("pricing.trunks", "trunks")}
                      </span>
                    </li>
                    <li className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3 w-3 text-[hsl(var(--ok))] shrink-0" />
                      <span>
                        {p.limits.cdr_retention_days
                          ? `${p.limits.cdr_retention_days}d ${t("pricing.cdrRetention", "CDR retention")}`
                          : t("pricing.cdrUnlimited", "Unlimited CDR retention")}
                      </span>
                    </li>
                    {Object.entries(p.features)
                      .filter(([, v]) => v)
                      .map(([k]) => (
                        <li key={k} className="flex items-center gap-1.5">
                          <CheckCircle2 className="h-3 w-3 text-[hsl(var(--ok))] shrink-0" />
                          <span>
                            {t(`plans.feature.${k}`, k.replace(/_/g, " "))}
                          </span>
                        </li>
                      ))}
                  </ul>
                  <Link
                    to={`/signup?plan=${p.slug}`}
                    className="block pt-2"
                  >
                    <Button
                      className="w-full"
                      variant={isFeatured ? "default" : "outline"}
                      data-testid={`pricing-cta-${p.slug}`}
                    >
                      {p.price_monthly_usd === 0
                        ? t("pricing.startFree", "Start free")
                        : t("pricing.choose", "Choose {{name}}", { name: p.name })}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12 text-xs text-muted-foreground space-y-1">
          <p>
            {t(
              "pricing.note1",
              "All tiers include all SIP codecs (G.711, G.722, Opus, GSM, AMR-WB) at no extra cost — we don't transcode."
            )}
          </p>
          <p>
            {t(
              "pricing.note2",
              "No per-minute fees. No DID charges. No hidden codec fees. Switch tiers anytime."
            )}
          </p>
          <p>
            {t(
              "pricing.note3",
              "Need more than 500 concurrent calls? Contact us for wholesale pricing."
            )}
          </p>
        </div>
      </section>
    </div>
  );
}
